import { AccountCircle, ArrowDropDown, ArrowForward } from "@mui/icons-material";
import MoneyIcon from "@mui/icons-material/AttachMoney";
import LandVision from "@mui/icons-material/Map";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import ReceiptIcon from "@mui/icons-material/Receipt";
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Menu,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { get, includes, orderBy } from "lodash";
import React, { useState } from "react";
import ReactGA from "react-ga4";
import { Link } from "react-router-dom";
import BSInput from "../../../common/js/components/BSInput";
import KMStatBox from "../../../common/js/components/KMStatBox";
import KMTools from "../../../common/js/components/KMTools";
import LeaseAndSaleDropdownOptions from "../../../common/js/components/LeaseAndSaleDropdownOptions";
import StatusChip from "../../../common/js/components/StatusChip";
import { Sorty } from "../../../common/js/icons";
import config from "../../../config";
import OutsideCompModal from "../comp/OutsideCompModal";
import RequestModalContainer from "../listing/RequestModalContainer";
import { formatDate, toDollarAmount } from "../utils/operations";
import "./dashboard.css";

const StyledPageToolBarInputs = styled("div")({
    display: "flex",
    alignItems: "center",
    gap: "0.5vw",
});

const StyledDateRangeHeaderText = styled("div")({
    fontSize: "13px",
    fontWeight: "600",
    color: "rgba(0, 0, 0, 0.54)",
    textTransform: "uppercase",
});

const StyledPageToolbar = styled("div")({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    margin: "35px 0px",
    minWidth: "900px",
});

const StyledDateRangeContainer = styled("div")({
    display: "flex",
    alignItems: "end",
    gap: "2vw",
    paddingLeft: "1vw",
});

const StyledIconInButton = styled(LandVision)({
    marginRight: "5px",
});

const StyledPaper = styled(Paper)({
    padding: "10px 20px",
    fontSize: 14,
    marginBottom: "15px",
});

const StyledSortable = styled("span")({
    paddingRight: "5px",
    paddingBottom: "3px",
});

const StyledIdLink = styled(Link)({
    "&:hover": {
        cursor: "pointer",
        textDecoration: "underline",
    },
    color: "#2C99D2",
    fontWeight: 600,
    textDecoration: "none",
});

const StyledLoadingPlaceholder = styled("div")({
    marginTop: "3em",
    marginBottom: "1.5em",
    textAlign: "center",
    width: "100%",
});

const StyledTableCell = styled(TableCell)({
    padding: "0px",
});

const StyledTdLinkText = styled("span")({
    fontWeight: "200",
});

const StyledGoToArrow = styled(ArrowForward)({
    color: "#2C99D2",
    display: "inline-block",
    "&:hover": {
        cursor: "pointer",
        textDecoration: "none",
    },
});

const StyledTableLink = styled("div")({
    display: "flex",
    gap: "1vw",
    alignItems: "center",
});

const StyledLvButton = styled("div")({
    right: "0px",
    bottom: "-19px",
    padding: "8px",
});

const BrokerDashboard = (props) => {
    const {
        brokerValues,
        closeDeal,
        dashboardParams,
        errors,
        handleDashboardInput,
        handleSort,
        history,
        loading,
        role,
        searchProperty,
        sort,
        toggleRequestModal,
    } = props;

    const [closeDealDialogOpen, setCloseDealDialogOpen] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [listingToClose, setListingToClose] = useState(null);
    const [listingAnchorEl, setListingAnchorEl] = useState(null);
    const openListingDropdown = Boolean(listingAnchorEl);

    const handleShowModal = (spaceType, transactionType) => {
        setShowModal(!showModal);
        toggleRequestModal(spaceType, transactionType);
        setListingAnchorEl(null);
    };

    const handleClickOpen = (listing) => {
        setCloseDealDialogOpen(true);
        setListingToClose(listing);
    };

    const handleCloseAndStart = async () => {
        const result = await closeDeal(listingToClose);
        if (result) {
            if (result.dealKey) {
                history.push(`/deal/${result.dealKey}`);
            }
        }
        handleClose();
    };

    const handleClose = () => {
        setCloseDealDialogOpen(false);
        setListingToClose(null);
    };

    const displayDealStep = (intStep) => {
        switch (intStep) {
            case 1:
                return "Comp";
            case 2:
                return "Voucher";
            default:
                return "";
        }
    };

    const handleListingDropdown = (event) => {
        setListingAnchorEl(event.currentTarget);
    };

    const handleCloseListingDropdown = () => {
        setListingAnchorEl(null);
    };

    const recentOpenDealsSortColumn = get(sort, "recentOpenDeals.columnName", "dealCreatedDate");
    const recentOpenDealsSortDirection = get(sort, "recentOpenDeals.sortDirection", "desc");
    const recentOpenListingsSortColumn = get(sort, "recentOpenListings.columnName", "dateAvailable");
    const recentOpenListingsSortDirection = get(sort, "recentOpenListings.sortDirection", "desc");

    return (
        <div>
            <StyledPageToolbar>
                <div>
                    <h2 className="page-title">Dashboard</h2>
                </div>
                <StyledDateRangeContainer>
                    <div>
                        <StyledDateRangeHeaderText>Dashboard Date Range</StyledDateRangeHeaderText>
                        <StyledPageToolBarInputs>
                            <BSInput
                                id="startDate"
                                value={get(dashboardParams, "startDate")}
                                type={"date"}
                                onChange={handleDashboardInput}
                                extraInputStyles={{
                                    backgroundColor: "white",
                                    marginTop: "1vh",
                                    width: "7vw",
                                    minWidth: "120px",
                                }}
                                extraFormControlStyles={{ marginBottom: "0" }}
                            />
                            <div style={{ color: "grey" }}>&mdash;</div>
                            <BSInput
                                id="endDate"
                                value={get(dashboardParams, "endDate")}
                                type={"date"}
                                onChange={handleDashboardInput}
                                extraInputStyles={{
                                    backgroundColor: "white",
                                    marginTop: "1vh",
                                    width: "7vw",
                                    minWidth: "120px",
                                }}
                                extraFormControlStyles={{ marginBottom: "0" }}
                            />
                        </StyledPageToolBarInputs>
                    </div>
                    <StyledPageToolBarInputs>
                        <div>
                            <Button
                                variant="contained"
                                color="primary"
                                href={config.LANDVISION_URL}
                                onClick={() => ReactGA.event("Navigate To Landvision from Dashboard")}
                            >
                                <StyledIconInButton /> Go to LandVision
                            </Button>
                        </div>
                        <div>
                            {includes(
                                [
                                    "broker",
                                    "portlandBrokerAdmin",
                                    "arizonaBrokerAdmin",
                                    "evp",
                                    "accounting",
                                    "propertyManagement",
                                    "transition",
                                    "general",
                                    "officeManager",
                                    "appraiser",
                                ],
                                role
                            ) && (
                                <React.Fragment>
                                    <Button
                                        id="Request-Listing-Dropdown"
                                        aria-controls={openListingDropdown ? "Request-Listing-Menu" : undefined}
                                        aria-haspopup="true"
                                        aria-expanded={openListingDropdown ? "true" : undefined}
                                        onClick={handleListingDropdown}
                                        variant="contained"
                                        endIcon={<ArrowDropDown />}
                                    >
                                        Request Listing
                                    </Button>
                                    <Menu
                                        id="Request-Listing-Menu"
                                        anchorEl={listingAnchorEl}
                                        open={openListingDropdown}
                                        onClose={handleCloseListingDropdown}
                                        anchorOrigin={{
                                            vertical: "bottom", // Align the top of the menu with the bottom of the button
                                            horizontal: "right", // Align the right edge of the menu with the right edge of the button
                                        }}
                                        transformOrigin={{
                                            vertical: "top", // Align the bottom of the menu with the top of the button
                                            horizontal: "right", // Align the left edge of the menu with the right edge of the button
                                        }}
                                        sx={{
                                            maxHeight: "80vh", // Set a max height for the dropdown
                                            overflowY: "none", // Enable vertical scrolling if items exceed max height
                                            "& .MuiMenuItem-root": {
                                                paddingRight: "5vw", // Add padding to all MenuItems
                                            },
                                        }}
                                    >
                                        <LeaseAndSaleDropdownOptions callback={handleShowModal} />
                                    </Menu>
                                </React.Fragment>
                            )}
                            <RequestModalContainer
                                {...props}
                                handleShowModal={handleShowModal}
                                showModal={showModal}
                                modalType="listing"
                            />
                        </div>
                        <StyledLvButton className={"dashboard"}>
                            <OutsideCompModal
                                {...props}
                                errors={errors}
                                searchFunction={searchProperty}
                                internal={true}
                                dashboard={true}
                            />
                        </StyledLvButton>
                    </StyledPageToolBarInputs>
                </StyledDateRangeContainer>
            </StyledPageToolbar>

            <div style={{ display: "flex", gap: "1vw", alignItems: "flex-start", justifyContent: "space-between" }}>
                <div style={{ flex: 2 }}>
                    <StyledPaper>
                        <div
                            style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flex: 2 }}
                        >
                            <div>
                                <h3>Recent Open Deals</h3>
                            </div>
                            <div style={{ position: "relative", width: "140px" }}>
                                <StyledIdLink to="/deals">
                                    <StyledTableLink>
                                        <div style={{ paddingTop: "0.25vh" }}> See all deals</div>
                                        <StyledGoToArrow />
                                    </StyledTableLink>
                                </StyledIdLink>
                            </div>
                        </div>

                        {loading.recentOpenDeals ? (
                            <StyledLoadingPlaceholder>
                                <CircularProgress color="primary" />
                            </StyledLoadingPlaceholder>
                        ) : (
                            <React.Fragment>
                                <Divider />
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{ width: "55%", padding: 0 }}>
                                                <div style={{ display: "flex", alignItems: "center" }}>
                                                    <StyledSortable>Deal</StyledSortable>
                                                </div>
                                            </TableCell>

                                            <TableCell style={{ width: "20%", padding: 0 }}>
                                                <div
                                                    style={{ display: "flex", alignItems: "center" }}
                                                    onClick={() => handleSort("recentOpenDeals", "dealStage")}
                                                >
                                                    <StyledSortable>Step</StyledSortable>
                                                    <div>
                                                        <Sorty />
                                                    </div>
                                                </div>
                                            </TableCell>

                                            <TableCell numeric style={{ width: "25%", padding: 0 }}>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        position: "relative",
                                                    }}
                                                    onClick={() => handleSort("recentOpenDeals", "dealStatus")}
                                                >
                                                    <StyledSortable style={{ right: "6px", position: "absolute" }}>
                                                        Status
                                                    </StyledSortable>
                                                    <div style={{ right: "0px", position: "absolute" }}>
                                                        <Sorty />
                                                    </div>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {orderBy(
                                            brokerValues.recentOpenDeals,
                                            recentOpenDealsSortColumn,
                                            recentOpenDealsSortDirection
                                        ).map((deal) => (
                                            <TableRow
                                                key={get(deal, "dealKey")}
                                                selectable="false"
                                                className="tableRow"
                                            >
                                                <StyledTableCell>
                                                    <StyledIdLink to={`/deal/${get(deal, "dealKey", "")}`}>
                                                        <div>
                                                            {`Deal No. ${get(deal, "dealKey")}`}
                                                            <StyledTdLinkText>
                                                                {get(deal, "consultingDealName", null) !== null
                                                                    ? ` | ${get(deal, "consultingDealName")}`
                                                                    : get(deal, "listingName", null) !== null
                                                                    ? ` | ${get(deal, "listingName")}`
                                                                    : ""}
                                                            </StyledTdLinkText>
                                                        </div>
                                                    </StyledIdLink>
                                                    <div>
                                                        {get(deal, "address1")} {get(deal, "address2")}{" "}
                                                        {get(deal, "city")} {get(deal, "state")} {get(deal, "zip")}
                                                    </div>
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    {displayDealStep(get(deal, "dealStage"))}
                                                </StyledTableCell>
                                                <StyledTableCell align="right">
                                                    <StatusChip status={get(deal, "dealStatus", "")} />
                                                </StyledTableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </React.Fragment>
                        )}
                    </StyledPaper>

                    <StyledPaper>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div>
                                <h3>Recent Open Listings</h3>
                            </div>
                            <div style={{ position: "relative", width: "140px" }}>
                                <StyledIdLink to="/listings">
                                    <StyledTableLink>
                                        <div>See all listings</div>
                                        <StyledGoToArrow />
                                    </StyledTableLink>
                                </StyledIdLink>
                            </div>
                        </div>

                        {loading.recentOpenListings ? (
                            <StyledLoadingPlaceholder>
                                <CircularProgress color="primary" />
                            </StyledLoadingPlaceholder>
                        ) : (
                            <React.Fragment>
                                <Divider />
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{ width: "55%", padding: 0 }}>
                                                <div style={{ display: "flex", alignItems: "center" }}>
                                                    <StyledSortable>Deal</StyledSortable>
                                                </div>
                                            </TableCell>

                                            <TableCell style={{ width: "20%", padding: 0 }}>
                                                <div
                                                    style={{ display: "flex", alignItems: "center" }}
                                                    onClick={() => handleSort("recentOpenListings", "dateAvailable")}
                                                >
                                                    <StyledSortable>Date Avail</StyledSortable>
                                                    <div>
                                                        <Sorty />
                                                    </div>
                                                </div>
                                            </TableCell>

                                            <TableCell></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {orderBy(
                                            brokerValues.recentOpenListings,
                                            recentOpenListingsSortColumn === "dateAvailable"
                                                ? [(x) => new Date(x[recentOpenListingsSortColumn])]
                                                : recentOpenDealsSortColumn,
                                            recentOpenListingsSortDirection
                                        ).map((listing) => (
                                            <TableRow
                                                key={get(listing, "listingKey")}
                                                selectable="false"
                                                className="tableRow"
                                            >
                                                <StyledTableCell>
                                                    <StyledIdLink to={`/listing/${get(listing, "listingKey", "")}`}>
                                                        <div>
                                                            {`Listing No. ${get(listing, "listingKey")}`}
                                                            <StyledTdLinkText>
                                                                {get(listing, "listingName", null) !== null
                                                                    ? ` | ${get(listing, "listingName")}`
                                                                    : ""}
                                                            </StyledTdLinkText>
                                                        </div>
                                                    </StyledIdLink>
                                                    <div>
                                                        {get(listing, "address1")} {get(listing, "address2")}{" "}
                                                        {get(listing, "city")} {get(listing, "state")}{" "}
                                                        {get(listing, "zip")}
                                                    </div>
                                                </StyledTableCell>

                                                <StyledTableCell>
                                                    {formatDate("MM-DD-YYYY")(get(listing, "dateAvailable", ""))}
                                                </StyledTableCell>

                                                <TableCell numeric style={{ position: "relative" }}>
                                                    <Button
                                                        style={{
                                                            position: "absolute",
                                                            right: "0px",
                                                            bottom: "12px",
                                                        }}
                                                        color="primary"
                                                        onClick={() => handleClickOpen(listing)}
                                                        variant="contained"
                                                    >
                                                        Close Deal
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </React.Fragment>
                        )}
                    </StyledPaper>
                </div>
                <div style={{ flex: 1 }}>
                    {loading.brokerYTDCommission ? (
                        <StyledLoadingPlaceholder>
                            <CircularProgress color="primary" />
                        </StyledLoadingPlaceholder>
                    ) : (
                        <KMStatBox
                            icon={<MoneyIcon />}
                            title="Total Commission"
                            tooltip="Total amount of Commission Allocations you have received for the specified date range."
                            number={toDollarAmount(
                                get(brokerValues, `brokerYTDCommission.${0}.calculatedValueA`),
                                "--"
                            )}
                        />
                    )}
                    {loading.brokerYTDListings ? (
                        <StyledLoadingPlaceholder>
                            <CircularProgress color="primary" />
                        </StyledLoadingPlaceholder>
                    ) : (
                        <KMStatBox
                            icon={<MeetingRoomIcon />}
                            title="Active Listings"
                            tooltip="Number of active listings on which you are included as a listing broker."
                            number={get(brokerValues, `brokerYTDListings`, "--")}
                        />
                    )}
                    {loading.brokerYTDClosedDeals ? (
                        <StyledLoadingPlaceholder>
                            <CircularProgress color="primary" />
                        </StyledLoadingPlaceholder>
                    ) : (
                        <KMStatBox
                            icon={<ReceiptIcon />}
                            title="Closed Deals"
                            tooltip="The number of Deals in which you have received a Commission Allocation during the specified date range."
                            number={get(brokerValues, `brokerYTDClosedDeals`, "--")}
                        />
                    )}
                    {loading.brokerLicenseExpiration ? (
                        <StyledLoadingPlaceholder>
                            <CircularProgress color="primary" />
                        </StyledLoadingPlaceholder>
                    ) : (
                        <KMStatBox
                            icon={<AccountCircle />}
                            title="License Exp Date"
                            number={formatDate(
                                "MM/DD/YYYY",
                                "--"
                            )(get(brokerValues, `brokerLicenseExpiration.expirationDate`, ""))}
                        />
                    )}
                    <KMTools />
                </div>
            </div>
            <Dialog
                open={closeDealDialogOpen}
                onClose={handleClose}
                aria-labelledby="close-deal"
                aria-describedby="close-deal-dialog"
            >
                <DialogTitle id="close-deal">{"Are you sure?"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Closing this deal will start the invoice process for this transaction and move the comp to a
                        closed status.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="primary" variant="outlined">
                        Disagree
                    </Button>
                    <Button onClick={handleCloseAndStart} color="secondary" autoFocus variant="contained">
                        Agree
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default BrokerDashboard;
